export type ContentBlock =
  | { type: "prose"; heading?: string; paragraphs: string[] }
  | { type: "cards"; heading?: string; items: { title: string; body: string }[] }
  | { type: "links"; heading?: string; items: { href: string; label: string; description?: string }[] }
  | { type: "faq"; heading?: string; items: { title: string; body: string }[] }
  | { type: "notice"; body: string };

export type ContentPage = {
  slug: string;
  group: string;
  label: string;
  title: string;
  description: string;
  eyebrow: string;
  heading: string;
  lead: string;
  blocks: ContentBlock[];
  noindex?: boolean;
};

const PRE_MARKET =
  "AiMediQ™ powered by datasyteAI™ is in the pre-market development and validation phase. It is not cleared or approved by the FDA or any other regulatory authority, is not available for sale, and is not for clinical use.";

export const contentPages: ContentPage[] = [
  {
    slug: "platform",
    group: "Platform",
    label: "Platform Overview",
    title: "Platform Overview | Intelligent Resuscitation Infrastructure",
    description:
      "How datasyteAI™ and AiMediQ™ work as one infrastructure layer connecting readiness, clinical response, documentation, data, and continuous improvement.",
    eyebrow: "Platform",
    heading: "One connected intelligence layer for the resuscitation lifecycle",
    lead:
      "datasyteAI™ is the clinical intelligence layer. AiMediQ™ brings it to the bedside. Together they are designed as Intelligent Resuscitation Infrastructure — one coordinated system spanning readiness, response, documentation, review, and improvement — while clinicians stay in control of every decision.",
    blocks: [
      {
        type: "cards",
        heading: "How the platform is organized",
        items: [
          {
            title: "Intelligence layer — datasyteAI™",
            body:
              "Protocol-aligned guidance trained to your institution's standards of care, expert review, and documented practice.",
          },
          {
            title: "Point of care — AiMediQ™",
            body:
              "The AI-powered Code Cart: the physical surface where prompts, timing, and medication context appear during an event.",
          },
          {
            title: "Record and review",
            body:
              "A structured event record designed to reduce reconstruction after the fact and support quality review.",
          },
          {
            title: "Readiness between events",
            body:
              "Simulation and drill support so teams practice with the same surface they use in a real event.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Design principles",
        paragraphs: [
          "Clinician-in-the-loop by default. The platform surfaces the next reasonable step; the team decides.",
          "Calm under pressure. Fewer words, larger targets, and no interface that has to be learned during an emergency.",
          "Local truth. Guidance is aligned to your protocols and reviewed by your experts, not to a generic default.",
        ],
      },
      {
        type: "links",
        heading: "Go deeper",
        items: [
          { href: "/datasyteai", label: "datasyteAI™", description: "The intelligence layer." },
          { href: "/aimediq", label: "AiMediQ™", description: "The AI-powered Code Cart." },
          { href: "/how-it-works", label: "How It Works", description: "The workflow, step by step." },
          {
            href: "/responsible-ai",
            label: "Responsible Clinical AI",
            description: "Human oversight, governance, data stewardship, and stated limitations.",
          },
        ],
      },
      { type: "notice", body: PRE_MARKET },
    ],
  },


  // Evidence

  // Resources
  {
    slug: "resources/insights",
    group: "Resources",
    label: "Insights",
    title: "Insights | AiMediQ™ powered by datasyteAI™",
    description:
      "Perspectives on resuscitation quality, decision support, and readiness from PathFree Technologies.",
    eyebrow: "Resources",
    heading: "Insights",
    lead: "Short pieces on why resuscitation response varies and what a decision-support surface can reasonably fix.",
    blocks: [
      {
        type: "links",
        heading: "Start with these",
        items: [
          { href: "/problem", label: "Why Code Blue outcomes vary", description: "The variation problem, stated plainly." },
          { href: "/how-it-works", label: "The workflow, step by step", description: "What the first minutes look like with support." },
          { href: "/evidence/clinical-approach", label: "Our clinical approach", description: "Guideline alignment and boundaries." },
        ],
      },
      {
        type: "prose",
        heading: "More coming",
        paragraphs: [
          "Additional articles are in preparation. Subscribe from the footer to be notified when new pieces publish.",
        ],
      },
    ],
  },
  {
    slug: "resources/news",
    group: "Resources",
    label: "News",
    title: "News | PathFree Technologies, datasyteAI™ and AiMediQ™",
    description: "Company and program updates from PathFree Technologies Corporation.",
    eyebrow: "Resources",
    heading: "News",
    lead: "Program milestones and company updates. Announcements appear here once they are confirmed.",
    blocks: [
      {
        type: "prose",
        heading: "Current status",
        paragraphs: [
          "AiMediQ™ powered by datasyteAI™ remains in the pre-market development and validation phase.",
          "For press or partnership inquiries, contact info@pathfree.com.",
        ],
      },
      {
        type: "links",
        heading: "Elsewhere",
        items: [
          { href: "https://pathfree.com", label: "PathFree Technologies", description: "Company site." },
          { href: "/evidence/regulatory-quality", label: "Regulatory and quality status" },
        ],
      },
    ],
  },
  {
    slug: "resources/downloads",
    group: "Resources",
    label: "Downloads",
    title: "Downloads | AiMediQ™ powered by datasyteAI™",
    description:
      "Overview materials and the interactive flip book for AiMediQ™ powered by datasyteAI™.",
    eyebrow: "Resources",
    heading: "Downloads and materials",
    lead: "Reference material for evaluation teams. Request anything you need that is not listed here.",
    blocks: [
      {
        type: "links",
        heading: "Available now",
        items: [
          { href: "/flip-book", label: "Interactive flip book", description: "Browse the full overview in your browser." },
          { href: "/video", label: "Video explainer", description: "What a Code Blue is and why response quality varies." },
          { href: "/contact", label: "Request materials", description: "Ask for a specific document or briefing." },
        ],
      },
      {
        type: "prose",
        heading: "Note",
        paragraphs: [
          "Detailed technical and security documentation is shared under a mutual agreement during evaluation.",
        ],
      },
    ],
  },
  {
    slug: "resources/faq",
    group: "Resources",
    label: "Frequently Asked Questions",
    title: "Frequently Asked Questions | AiMediQ™ powered by datasyteAI™",
    description:
      "Answers on regulatory status, scope, safety boundaries, security, and how to evaluate AiMediQ™ powered by datasyteAI™.",
    eyebrow: "Resources",
    heading: "Frequently asked questions",
    lead: "Direct answers to the questions we hear most from clinical, quality, and IT reviewers.",
    blocks: [
      {
        type: "faq",
        items: [
          {
            title: "Is AiMediQ™ available for clinical use today?",
            body:
              "No. It is in the pre-market development and validation phase, is not FDA cleared or approved, is not available for sale, and is limited to investigational, research, simulation, and training use.",
          },
          {
            title: "Does the system make clinical decisions?",
            body:
              "No. It is decision support. It presents protocol-aligned next steps and timing cues; the clinical team decides and can override or ignore any prompt.",
          },
          {
            title: "Whose protocols does it follow?",
            body:
              "Yours. Guidance is configured against your institution's approved protocols and reviewed by your experts before use.",
          },
          {
            title: "What is the relationship between the brands?",
            body:
              "PathFree Technologies Corporation is the company. datasyteAI™ is the clinical intelligence layer. AiMediQ™ is the AI-powered Code Cart it powers.",
          },
          {
            title: "How is patient data handled?",
            body:
              "The platform is designed around data minimization, role-scoped access, and encryption in transit and at rest. Specific data flows are documented per engagement.",
          },
          {
            title: "Do you claim improved survival?",
            body:
              "No. We make no clinical outcome claims. Results will be published only when completed study data supports them.",
          },
          {
            title: "How do we evaluate it?",
            body:
              "Start with a demo and a walkthrough of your current code process. Evaluations during pre-market are scoped to simulation and training.",
          },
        ],
      },
      { type: "notice", body: PRE_MARKET },
    ],
  },

  // Company
  // Corporate / investor
  {
    slug: "corporate/investor-information",
    group: "Corporate",
    label: "Investor Information",
    title: "Corporate and Investor Information | PathFree Technologies Corporation",
    description:
      "Corporate and investor information for PathFree Technologies Corporation, kept separate from the datasyteAI™ and AiMediQ™ customer journey.",
    eyebrow: "Corporate",
    heading: "Corporate and investor information",
    lead:
      "This page is intentionally separate from our hospital and clinical content. Nothing here is an offer to sell or a solicitation to buy any security.",
    blocks: [
      {
        type: "links",
        heading: "Corporate materials",
        items: [
          { href: "/market", label: "Market opportunity", description: "Market context for AI-assisted resuscitation support." },
          { href: "/business-model", label: "Business model", description: "How the platform is intended to be delivered." },
          { href: "/competition", label: "Competitive landscape", description: "How our approach differs." },
          { href: "https://pathfree.com/investors", label: "PathFree investor relations", description: "Corporate investor page." },
        ],
      },
      {
        type: "prose",
        heading: "Forward-looking statements",
        paragraphs: [
          "Statements about product capability, market size, regulatory progress, and financial expectations are forward-looking and subject to change.",
          "This page is informational only. It is not an offer to sell or a solicitation of an offer to buy any security, and any such offer would be made only through authorized documentation.",
        ],
      },
      { type: "notice", body: PRE_MARKET },
    ],
  },

  // Legal / utility
  {
    slug: "accessibility",
    group: "Legal",
    label: "Accessibility",
    title: "Accessibility Statement | datasyte.ai",
    description:
      "Accessibility commitment, current conformance target, and how to report an accessibility barrier on datasyte.ai.",
    eyebrow: "Legal",
    heading: "Accessibility statement",
    lead:
      "We want datasyte.ai to be usable by everyone, including people using screen readers, keyboard-only navigation, and magnification.",
    blocks: [
      {
        type: "prose",
        heading: "Our target",
        paragraphs: [
          "We aim to meet WCAG 2.2 Level AA. Accessibility is reviewed as part of ongoing site work rather than as a one-time audit.",
          "Current practices include semantic headings and landmarks, visible focus indicators, keyboard-operable navigation and menus, text alternatives for meaningful images, and color contrast checked against AA thresholds.",
        ],
      },
      {
        type: "prose",
        heading: "Known limitations",
        paragraphs: [
          "Some embedded third-party content, including the interactive flip book and hosted video, is outside our direct control and may not fully conform. We are happy to provide the same information in an accessible format on request.",
        ],
      },
      {
        type: "prose",
        heading: "Report a barrier",
        paragraphs: [
          "Email info@pathfree.com or call +1 (949) 903-3788 and describe the page and the barrier. We will respond and provide the content another way while we fix the issue.",
        ],
      },
    ],
  },
  {
    slug: "trademarks",
    group: "Legal",
    label: "Trademarks",
    title: "Trademarks | PathFree Technologies Corporation",
    description:
      "Trademark and brand usage information for PathFree Technologies Corporation, datasyteAI™, and AiMediQ™.",
    eyebrow: "Legal",
    heading: "Trademarks",
    lead:
      "datasyteAI™ and AiMediQ™ are trademarks of PathFree Technologies Corporation, Irvine, California.",
    blocks: [
      {
        type: "prose",
        heading: "Our marks",
        paragraphs: [
          "datasyteAI™ and AiMediQ™ are trademarks of PathFree Technologies Corporation. PathFree™ and the PathFree and datasyteAI logos are also marks of PathFree Technologies Corporation.",
          "Correct casing matters: write datasyteAI™ and AiMediQ™ exactly as shown, with the trademark symbol on first prominent use.",
        ],
      },
      {
        type: "prose",
        heading: "Permitted use",
        paragraphs: [
          "You may refer to our products by name in factual, non-misleading statements. You may not use our marks or logos in a way that suggests endorsement, partnership, certification, or affiliation without written permission.",
          "Requests for logo files or brand permission: info@pathfree.com.",
        ],
      },
      {
        type: "prose",
        heading: "Third-party marks",
        paragraphs: [
          "All other product and company names mentioned on this site may be trademarks of their respective owners. Reference to them does not imply any affiliation or endorsement.",
        ],
      },
    ],
  },
];

export const contentPageBySlug = Object.fromEntries(
  contentPages.map((page) => [page.slug, page]),
) as Record<string, ContentPage>;
